import React, { useEffect } from "react";
import { connect } from "react-redux";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import BaseMapStyle, { isDarkMapStyle } from "@mapmycustomers/shared/enum/map/BaseMapStyle";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { AreaFilterCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { convertLongLatToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";

import { useMap } from "@app/component/map/FeaturedMap";
import useMapView from "@app/component/view/utils/useMapView";
import { getMapStyle } from "@app/scene/map/store/selectors";
import { fetchCompany } from "@app/store/company/actions";
import { fetchPerson } from "@app/store/person/actions";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import { UniversalFieldName } from "@app/util/fieldModel/universalFieldsFieldModel";
import { isEntityValue } from "@app/util/filters/Location/assert";
import { isSimpleCondition } from "@app/util/viewModel/assert";

interface Props {
  mapStyle: BaseMapStyle;
  onFetchCompany: typeof fetchCompany;
  onFetchPerson: typeof fetchPerson;
}

const LocationFilterCircle: React.FC<Props> = ({ mapStyle, onFetchCompany, onFetchPerson }) => {
  const { map } = useMap();
  const {
    viewState: { filter },
  } = useMapView();
  const color = isDarkMapStyle(mapStyle) ? colors.white : colors.softSlate;

  useEffect(
    () => {
      if (
        !filter.universal?.[UniversalFieldName.GEOPOINT] ||
        !isSimpleCondition(filter.universal[UniversalFieldName.GEOPOINT])
      ) {
        return;
      }

      const filterCondition = filter.universal[UniversalFieldName.GEOPOINT] as AreaFilterCondition;
      if ([FilterOperator.EMPTY, FilterOperator.NOT_EMPTY].includes(filterCondition.operator)) {
        return; // nothing to display here
      }

      const filterValue = filterCondition.value;

      const circleObject: { circle: google.maps.Circle | undefined } = { circle: undefined };

      (isEntityValue(filterValue)
        ? new Promise<LongLat | undefined>((resolve) => {
            if (filterValue.entity.type === EntityType.COMPANY) {
              onFetchCompany({
                id: filterValue.entity.id,
                callback: (company: Company) => resolve(company.geoPoint?.coordinates),
                failureCallback: () => resolve(undefined),
              });
            } else if (filterValue.entity.type === EntityType.PERSON) {
              onFetchPerson({
                id: filterValue.entity.id,
                callback: (person: Person) => resolve(person.geoPoint?.coordinates),
                failureCallback: () => resolve(undefined),
              });
            }
          })
        : Promise.resolve([filterValue.longitude, filterValue.latitude] as LongLat)
      ).then((coordinates) => {
        if (!coordinates) {
          return;
        }
        circleObject.circle = new google.maps.Circle({
          center: convertLongLatToLatLngLiteral(coordinates),
          clickable: false,
          fillColor: color,
          fillOpacity: 0.2,
          map,
          radius: isEntityValue(filterValue)
            ? filterValue.distanceInMeters
            : filterValue.distanceInMeters,
          strokeColor: color,
          strokeOpacity: 1,
          strokeWeight: 1,
        });
        map.fitBounds(circleObject.circle!.getBounds()!);
      });
      return () => {
        circleObject.circle?.setMap(null);
      };
    },
    // disabling check because react insists that we add a dep on filters.universal,
    // but we don't need that
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [color, filter?.universal?.[UniversalFieldName.GEOPOINT], map]
  );

  return null;
};

const mapDispatchToProps = {
  onFetchCompany: fetchCompany,
  onFetchPerson: fetchPerson,
};

const mapStateToProps = (state: RootState) => ({
  mapStyle: getMapStyle(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(LocationFilterCircle);
